import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@kidshome/ui';
import { tDynamic } from '@/shared/lib/i18n';

/* This file exports the component PLUS two tiny id helpers the panel needs to cross-reference the tabs;
   keeping them co-located reads better than a separate module, so opt out of the refresh-only rule. */
/* eslint-disable react-refresh/only-export-components */

/**
 * Stable DOM ids so the tabs and the grid panel can cross-reference each other (WAI-ARIA tabs pattern).
 * An `idScope` namespaces them so MULTIPLE tab bars can coexist on one page (the platform renders one per
 * hero) without duplicate ids / a shared dangling panel; the kid wardrobe (one bar) omits it.
 */
export const wardrobeTabId = (category: string, idScope = ''): string =>
  `${idScope ? `${idScope}-` : ''}wtab-${category}`;
export const wardrobePanelId = (idScope = ''): string =>
  `${idScope ? `${idScope}-` : ''}wardrobe-panel`;

/**
 * A tiny wear-place glyph (decorative; the label always carries the meaning). Heroes are SKINS now
 * (ADR-0110) — a hero declares its own place ids (`royal_look`/`royal_scene`/`dash_look`/…), so the glyph is
 * chosen by the place's SEMANTIC SUFFIX rather than a hardcoded per-category map: a `*_look` full-figure swap
 * gets a figure silhouette, a `*_scene`/`background` backdrop gets a sun-over-hills, anything else a neutral
 * diamond. New heroes get sensible tab icons with zero edits here.
 */
const LOOK_GLYPH = <path d="M9 4 q3 -2 6 0 l2 3 -2 2 2 9 H7 l2 -9 -2 -2 Z" />;
const SCENE_GLYPH = (
  <g>
    <circle cx="16" cy="8" r="3" />
    <path d="M2 16 q5 -3 10 0 t10 0 V20 H2 Z" />
  </g>
);
const DIAMOND_GLYPH = <path d="M12 3 l7 9 -7 9 -7 -9 Z" />;

function glyphForPlace(placeId: string): React.ReactElement {
  if (placeId === 'background' || placeId.endsWith('_scene')) return SCENE_GLYPH;
  if (placeId.endsWith('_look')) return LOOK_GLYPH;
  return DIAMOND_GLYPH;
}

/**
 * The wardrobe PLACE TAB BAR (ADR-0098 wardrobe redesign · ADR-0110 skin heroes) — a non-scrolling
 * segmented strip of the chosen hero's declared wear-places (royal: Looks + Scene). Tapping a tab swaps the
 * grid below to ONLY that place, so the kid reaches any item in one tap instead of scrolling past the other
 * sections (the research-backed "pinned stage + tabs + grid" layout). The tab set is DATA-DRIVEN — the hero
 * declares its own places, so this never hardcodes a category list. Glyph + word so 4-7yo non-readers and
 * readers both get a cue. Full WAI-ARIA tabs pattern: `role=tablist`/`tab` + `aria-selected`, `aria-controls` to
 * the grid panel, roving `tabIndex` (only the active tab is Tab-reachable), and Left/Right/Home/End
 * arrow-key navigation that moves the selection + focus together.
 */
export function WardrobeTabBar({
  categories,
  active,
  onSelect,
  idScope = '',
  label,
}: {
  categories: readonly string[];
  active: string;
  onSelect: (category: string) => void;
  /** Namespaces the tab/panel ids so several bars can share a page (one per hero on the platform). */
  idScope?: string;
  /** The tablist's accessible name; defaults to the kid "Customize" label when omitted. */
  label?: string;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (toIndex: number): void => {
    const i = (toIndex + categories.length) % categories.length;
    const next = categories[i];
    if (next === undefined) return;
    onSelect(next);
    tabRefs.current[i]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number): void => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(categories.length - 1);
        break;
    }
  };

  return (
    <div className="wardrobe-tabs" role="tablist" aria-label={label ?? t('shop.title')}>
      {categories.map((category, index) => {
        const selected = category === active;
        return (
          <button
            key={category}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={wardrobeTabId(category, idScope)}
            aria-selected={selected}
            aria-controls={wardrobePanelId(idScope)}
            tabIndex={selected ? 0 : -1}
            className={cn('wardrobe-tab', selected && 'is-on')}
            onClick={() => onSelect(category)}
            onKeyDown={(e) => onKeyDown(e, index)}
          >
            <svg className="tab-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              {glyphForPlace(category)}
            </svg>
            <span className="tab-label">
              {tDynamic(t, `common:cosmetics.category.${category}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
